import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { fmtMoney } from "@/lib/format";

const TABS = ["Overview", "Photos", "Documents", "Deals", "Activity"];

export default function PropertyDetail() {
    const { id } = useParams();
    const [p, setP] = useState(null);
    const [tab, setTab] = useState("Overview");
    const [deals, setDeals] = useState([]);
    const [acts, setActs] = useState([]);

    const load = async () => {
        const [pr, dr, ar] = await Promise.all([
            api.get(`/properties/${id}`),
            api.get(`/deals?property_id=${id}`),
            api.get(`/activities?property_id=${id}`),
        ]);
        setP(pr.data); setDeals(dr.data.items || []); setActs(ar.data);
    };
    useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

    const uploadPhoto = async (e) => {
        const f = e.target.files?.[0];
        if (!f) return;
        const fd = new FormData();
        fd.append("file", f);
        try {
            await api.post(`/properties/${id}/photos`, fd, { headers: { "Content-Type": "multipart/form-data" } });
            toast.success("Uploaded");
            load();
        } catch (err) { toast.error(err?.response?.data?.detail || "Upload failed"); }
    };

    if (!p) return <div className="p-8 text-sm text-muted-foreground">Loading…</div>;

    return (
        <div className="p-6 md:p-10" data-testid="page-property-detail">
            <Link to="/app/properties" className="text-xs text-muted-foreground hover:opacity-70">← Back to properties</Link>
            <div className="overline mt-4">{p.property_type || "Property"}</div>
            <h1 className="mt-2 font-display text-4xl tracking-tight leading-none">{p.address_line1}</h1>
            <div className="mt-2 text-sm text-muted-foreground">{[p.city, p.state, p.postal_code].filter(Boolean).join(", ")}</div>
            <div className="mt-3 text-xs text-muted-foreground">Data source: {p.data_source} · Last verified: {new Date(p.last_verified_at || p.created_at).toLocaleString()}</div>

            <div className="mt-6 flex flex-wrap gap-1 border-b border-border">
                {TABS.map((tName) => (
                    <button key={tName} onClick={() => setTab(tName)} className={`px-4 py-3 text-sm border-b-2 -mb-px ${tab === tName ? "border-foreground text-foreground" : "border-transparent text-muted-foreground hover:text-foreground"}`} data-testid={`property-tab-${tName.toLowerCase()}`}>
                        {tName}
                    </button>
                ))}
            </div>

            <div className="mt-8">
                {tab === "Overview" && (
                    <div className="grid md:grid-cols-2 gap-6 max-w-3xl">
                        <Row label="Lot sqft" value={p.lot_size_sqft} />
                        <Row label="Building sqft" value={p.building_size_sqft} />
                        <Row label="Year built" value={p.year_built} />
                        <Row label="Zoning" value={p.zoning} />
                        <Row label="Flood zone" value={p.flood_zone} />
                        <Row label="Parcel #" value={p.parcel_number} />
                        <Row label="Assessed value" value={p.assessed_value ? fmtMoney(p.assessed_value) : null} />
                        <Row label="Appraised value" value={p.appraised_value ? fmtMoney(p.appraised_value) : null} />
                    </div>
                )}
                {tab === "Photos" && (
                    <div>
                        <label className="inline-flex items-center border border-border px-4 py-2 text-sm cursor-pointer hover:bg-secondary" data-testid="upload-photo">
                            Upload photo
                            <input type="file" accept="image/*" onChange={uploadPhoto} className="hidden" />
                        </label>
                        <div className="mt-6 grid grid-cols-2 md:grid-cols-4 gap-3">
                            {(p.photos || []).map((ph) => (
                                <div key={ph.id} className="border border-border overflow-hidden aspect-square">
                                    <img src={ph.data_url} alt={ph.filename} className="w-full h-full object-cover" />
                                </div>
                            ))}
                            {(!p.photos || p.photos.length === 0) && <div className="text-sm text-muted-foreground">No photos yet.</div>}
                        </div>
                    </div>
                )}
                {tab === "Documents" && (
                    <div className="text-sm text-muted-foreground">Document uploads accepted via API. Rich UI arrives in Phase 3.</div>
                )}
                {tab === "Deals" && (
                    <div>
                        <Link to={`/app/deals/new?property_id=${id}`} className="inline-flex bg-foreground text-background px-4 py-2 text-sm hover:opacity-90" data-testid="property-new-deal">Run a deal analysis</Link>
                        <div className="mt-6 space-y-3">
                            {deals.length === 0 && <div className="text-sm text-muted-foreground">No deals on this property yet.</div>}
                            {deals.map((d) => (
                                <Link key={d.id} to={`/app/deals/${d.id}`} className="block border border-border p-4 hover:bg-secondary/40" data-testid={`deal-row-${d.id}`}>
                                    <div className="flex justify-between">
                                        <div>
                                            <div className="overline">{d.deal_type.replace(/_/g, " ")}</div>
                                            <div className="font-medium">{d.name}</div>
                                        </div>
                                        <div className="text-xs text-muted-foreground">{d.status}</div>
                                    </div>
                                </Link>
                            ))}
                        </div>
                    </div>
                )}
                {tab === "Activity" && (
                    <div className="space-y-3">
                        {acts.length === 0 && <div className="text-sm text-muted-foreground">No activity yet.</div>}
                        {acts.map((a) => (
                            <div key={a.id} className="border border-border p-4">
                                <div className="overline">{a.kind}</div>
                                <div className="font-medium mt-2 text-sm">{a.title}</div>
                                <div className="text-xs text-muted-foreground mt-1">{new Date(a.occurred_at).toLocaleString()}</div>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
}

function Row({ label, value }) {
    return (
        <div className="border-b border-border pb-2">
            <div className="overline">{label}</div>
            <div className="mt-1 text-sm">{value || "—"}</div>
        </div>
    );
}
